import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../hooks/useApi';
import CaseStudiesPanel from './CaseStudiesPanel';
import CertificateUploadManager from './CertificateUploadManager';

const getInitials = (user) => {
    if (!user) return '';
    const first = (user.first_name || '').trim();
    const last = (user.last_name || '').trim();
    const initials = `${first.charAt(0)}${last.charAt(0)}`.trim();
    if (initials) return initials.toUpperCase();
    return (user.email || '?').charAt(0).toUpperCase();
};

const buildValue = (value) => {
    const trimmed = (value ?? '').toString().trim();
    return trimmed ? trimmed : null;
};

const resolveAvatarUrl = (url, baseUrl) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!baseUrl) return url;
    return `${baseUrl}${url}`;
};

const isImageUrl = (url) => /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(url || '');

const formatMarketplaceType = (value) => {
    if (!value) return '';
    if (value === 'ozon') return 'Ozon';
    if (value === 'wb') return 'Wildberries';
    return value.toString();
};

const formatRoleLabel = (value) => {
    if (!value) return '';
    if (value === 'owner') return 'Владелец';
    if (value === 'admin') return 'Администратор';
    if (value === 'admin_api') return 'Администратор с API';
    if (value === 'manager') return 'Менеджер';
    if (value === 'finance') return 'Финансист';
    if (value === 'viewer') return 'Наблюдатель';
    return value.toString();
};

const clampPercent = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return Math.min(100, Math.max(0, num));
};

const getCropRect = (image, crop) => {
    const imgW = image.naturalWidth || image.width;
    const imgH = image.naturalHeight || image.height;
    const baseSize = Math.min(imgW, imgH);
    const cropSize = baseSize / crop.zoom;
    const maxOffsetX = (imgW - cropSize) / 2;
    const maxOffsetY = (imgH - cropSize) / 2;
    const rawX = (imgW - cropSize) / 2 + crop.offsetX * maxOffsetX;
    const rawY = (imgH - cropSize) / 2 + crop.offsetY * maxOffsetY;
    const cropX = Math.min(Math.max(rawX, 0), imgW - cropSize);
    const cropY = Math.min(Math.max(rawY, 0), imgH - cropSize);
    return { cropX, cropY, cropSize };
};

const UserCabinet = React.memo(({
    user,
    summary,
    stats,
    busy,
    avatarBusy,
    summaryBusy,
    onSave,
    onAvatarUpload,
    onCopy,
    openLightbox,
    showStatus,
    onProfileUpdate
}) => {
    const { API_BASE_URL } = useApi();
    const certificateUrls = useMemo(() => user?.certificate_urls || [], [user?.certificate_urls]);
    const certificateImages = useMemo(
        () => certificateUrls.filter((url) => isImageUrl(url)),
        [certificateUrls]
    );
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        telegram_id: '',
        bio: '',
        agency_name: '',
        password: '',
        password_confirm: '',
        current_password: '',
        public_enabled: false,
        public_slug: '',
        public_show_email: true,
        public_show_phone: true,
        public_show_telegram: true,
        public_show_bio: true,
        public_show_agency: true,
        public_show_certificates: true,
        public_show_dashboard_projects: false,
        public_show_dashboard_cabinets: false
    });
    const [notice, setNotice] = useState(null);
    const cropCanvasRef = useRef(null);
    const [avatarCrop, setAvatarCrop] = useState({
        open: false,
        src: '',
        zoom: 1,
        offsetX: 0,
        offsetY: 0
    });

    useEffect(() => {
        if (!user) return;
        setForm((prev) => ({
            ...prev,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            telegram_id: user.telegram_id || '',
            bio: user.bio || '',
            agency_name: user.agency_name || '',
            password: '',
            password_confirm: '',
            current_password: '',
            public_enabled: Boolean(user.public_enabled),
            public_slug: user.public_slug || '',
            public_show_email: Boolean(user.public_show_email),
            public_show_phone: Boolean(user.public_show_phone),
            public_show_telegram: Boolean(user.public_show_telegram),
            public_show_bio: Boolean(user.public_show_bio),
            public_show_agency: Boolean(user.public_show_agency),
            public_show_certificates: Boolean(user.public_show_certificates),
            public_show_dashboard_projects: Boolean(user.public_show_dashboard_projects),
            public_show_dashboard_cabinets: Boolean(user.public_show_dashboard_cabinets)
        }));
        setNotice(null);
    }, [user?.id, user?.updated_at]);

    const initials = useMemo(() => getInitials(user), [user]);
    const avatarUrl = useMemo(
        () => resolveAvatarUrl(user?.avatar_url || '', API_BASE_URL),
        [user?.avatar_url, API_BASE_URL]
    );

    const displayName = useMemo(() => {
        if (!user) return '';
        const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
        return name || user.email || 'Без имени';
    }, [user]);
    const passwordMismatch =
        form.password && form.password_confirm && form.password !== form.password_confirm;
    const publicUrl = useMemo(() => {
        if (!user?.public_slug || typeof window === 'undefined') return '';
        return `${window.location.origin}/user/${user.public_slug}`;
    }, [user?.public_slug]);
    const onTimePercent = useMemo(
        () => clampPercent(stats?.tasks_on_time_percent ?? 0),
        [stats?.tasks_on_time_percent]
    );

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setNotice(null);
    };

    const handleAvatarInput = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setAvatarCrop({
                open: true,
                src: reader.result,
                zoom: 1,
                offsetX: 0,
                offsetY: 0
            });
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    const applyAvatarCrop = async () => {
        if (!avatarCrop.src) return;
        const image = new Image();
        image.src = avatarCrop.src;
        await new Promise((resolve) => {
            image.onload = resolve;
        });
        const { cropX, cropY, cropSize } = getCropRect(image, avatarCrop);

        const outputSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = outputSize;
        canvas.height = outputSize;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(
            image,
            cropX,
            cropY,
            cropSize,
            cropSize,
            0,
            0,
            outputSize,
            outputSize
        );
        canvas.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], 'avatar.png', { type: 'image/png' });
            onAvatarUpload?.(file);
            setAvatarCrop((prev) => ({ ...prev, open: false }));
        }, 'image/png');
    };

    useEffect(() => {
        if (!avatarCrop.open || !avatarCrop.src) return;
        const canvas = cropCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const image = new Image();
        image.src = avatarCrop.src;
        image.onload = () => {
            const { cropX, cropY, cropSize } = getCropRect(image, avatarCrop);
            const size = canvas.width;
            ctx.clearRect(0, 0, size, size);
            ctx.drawImage(image, cropX, cropY, cropSize, cropSize, 0, 0, size, size);
        };
    }, [avatarCrop]);


    const handleReset = () => {
        if (!user) return;
        setForm((prev) => ({
            ...prev,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            telegram_id: user.telegram_id || '',
            bio: user.bio || '',
            agency_name: user.agency_name || '',
            password: '',
            password_confirm: '',
            current_password: '',
            public_enabled: Boolean(user.public_enabled),
            public_slug: user.public_slug || '',
            public_show_email: Boolean(user.public_show_email),
            public_show_phone: Boolean(user.public_show_phone),
            public_show_telegram: Boolean(user.public_show_telegram),
            public_show_bio: Boolean(user.public_show_bio),
            public_show_agency: Boolean(user.public_show_agency),
            public_show_certificates: Boolean(user.public_show_certificates),
            public_show_dashboard_projects: Boolean(user.public_show_dashboard_projects),
            public_show_dashboard_cabinets: Boolean(user.public_show_dashboard_cabinets)
        }));
        setNotice(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!user) return;
        if (passwordMismatch) {
            setNotice({ type: 'error', message: 'Пароли не совпадают.' });
            return;
        }

        const payload = {};
        const fields = [
            'first_name',
            'last_name',
            'email',
            'phone',
            'telegram_id',
            'bio',
            'public_enabled',
            'public_slug',
            'public_show_email',
            'public_show_phone',
            'public_show_telegram',
            'public_show_bio',
            'public_show_agency',
            'public_show_certificates',
            'public_show_dashboard_projects',
            'public_show_dashboard_cabinets'
        ];

        fields.forEach((field) => {
            const current = user[field];
            let next = form[field];
            if (field === 'public_slug') {
                const trimmed = (next || '').trim();
                if (!trimmed) return;
                next = trimmed;
            }
            if (current !== next) {
                payload[field] = typeof next === 'string' ? buildValue(next) : next;
            }
        });

        if (form.password) {
            if (!form.current_password) {
                setNotice({ type: 'error', message: 'Введите текущий пароль, чтобы сменить его.' });
                return;
            }
            payload.password = form.password;
            payload.current_password = form.current_password;
        }

        if (payload.email && !window.confirm('Сменить email? На новый адрес придёт письмо, до подтверждения часть действий будет недоступна.')) {
            return;
        }

        if (!Object.keys(payload).length) {
            setNotice({ type: 'info', message: 'Нет изменений для сохранения.' });
            return;
        }

        if (payload.public_enabled && !user.public_enabled) {
            if (!window.confirm('Сделать профиль публичным?')) {
                return;
            }
        }

        if (
            payload.public_slug &&
            payload.public_slug !== user.public_slug &&
            !user.public_slug_changed
        ) {
            if (!window.confirm('Публичную ссылку можно изменить только один раз. Продолжить?')) {
                return;
            }
        }

        try {
            await onSave(payload);
            setForm((prev) => ({
                ...prev,
                password: '',
                password_confirm: '',
                current_password: ''
            }));
            setNotice(null);
        } catch (error) {
            setNotice({ type: 'error', message: 'Не удалось сохранить профиль.' });
        }
    };

    if (!user) {
        return (
            <div className="panel">
                <div className="empty">Профиль не загружен. Войдите в систему.</div>
            </div>
        );
    }

    return (
        <div className="user-cabinet">
            <div className="panel">
                <div className="panel__head user-cabinet__head">
                    <div>
                        <h3>Личный кабинет</h3>
                        <p className="muted">Настройте профиль, публичную страницу и параметры отображения.</p>
                    </div>
                    <div className="user-cabinet__head-actions" />
                </div>
            </div>

            {avatarCrop.open && (
                <div className="panel user-cabinet__crop">
                    <div className="panel__head">
                        <h4>Кадрирование аватара</h4>
                    </div>
                    <div className="user-cabinet__crop-body">
                        <div className="user-cabinet__crop-preview">
                            <canvas ref={cropCanvasRef} width="220" height="220" />
                        </div>
                        <div className="user-cabinet__crop-controls">
                            <label>
                                Масштаб
                                <input
                                    type="range"
                                    min="1"
                                    max="2.5"
                                    step="0.05"
                                    value={avatarCrop.zoom}
                                    onChange={(event) =>
                                        setAvatarCrop((prev) => ({ ...prev, zoom: Number(event.target.value) }))
                                    }
                                />
                            </label>
                            <label>
                                Сдвиг по горизонтали
                                <input
                                    type="range"
                                    min="-1"
                                    max="1"
                                    step="0.05"
                                    value={avatarCrop.offsetX}
                                    onChange={(event) =>
                                        setAvatarCrop((prev) => ({ ...prev, offsetX: Number(event.target.value) }))
                                    }
                                />
                            </label>
                            <label>
                                Сдвиг по вертикали
                                <input
                                    type="range"
                                    min="-1"
                                    max="1"
                                    step="0.05"
                                    value={avatarCrop.offsetY}
                                    onChange={(event) =>
                                        setAvatarCrop((prev) => ({ ...prev, offsetY: Number(event.target.value) }))
                                    }
                                />
                            </label>
                            <div className="user-cabinet__crop-actions">
                                <button type="button" onClick={applyAvatarCrop} disabled={avatarBusy}>
                                    Применить
                                </button>
                                <button
                                    type="button"
                                    className="ghost"
                                    onClick={() => setAvatarCrop((prev) => ({ ...prev, open: false }))}
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="user-cabinet__grid">
                <div className="panel">
                    <div className="panel__head">
                        <div>
                            <h4>Профиль</h4>
                            <p className="muted">Укажите данные и контакты для отображения.</p>
                        </div>
                    </div>
                    <form className="panel__form user-cabinet__form" onSubmit={handleSubmit}>
                        <div className="user-cabinet__fields">
                            <label>
                                Имя
                                <input
                                    type="text"
                                    value={form.first_name}
                                    onChange={handleChange('first_name')}
                                    placeholder="Введите имя"
                                    autoComplete="given-name"
                                />
                            </label>
                            <label>
                                Фамилия
                                <input
                                    type="text"
                                    value={form.last_name}
                                    onChange={handleChange('last_name')}
                                    placeholder="Введите фамилию"
                                    autoComplete="family-name"
                                />
                            </label>
                        </div>

                        <div className="user-cabinet__divider" />

                        <div className="user-cabinet__fields">
                            <label>
                                Email
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange('email')}
                                    placeholder="name@example.com"
                                    autoComplete="email"
                                />
                            </label>
                            <label>
                                Телефон
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={handleChange('phone')}
                                    placeholder="+7 900 000-00-00"
                                    autoComplete="tel"
                                />
                            </label>
                            <label>
                                Telegram
                                <input
                                    type="text"
                                    value={form.telegram_id}
                                    onChange={handleChange('telegram_id')}
                                    placeholder="@username или username"
                                />
                            </label>
                            <label>
                                Агентство
                                <input
                                    type="text"
                                    value={form.agency_name}
                                    onChange={handleChange('agency_name')}
                                    placeholder="Связка с агентством подключится позже"
                                    disabled
                                />
                            </label>
                        </div>

                        <label>
                            О себе
                            <textarea
                                rows={4}
                                value={form.bio}
                                onChange={handleChange('bio')}
                                placeholder="Коротко о себе и опыте"
                            />
                        </label>
                        <CertificateUploadManager
                            userId={user?.id}
                            currentUrls={certificateUrls}
                            onUpdate={onProfileUpdate}
                            onStatusChange={showStatus}
                        />

                        <div className="user-cabinet__divider" />

                        <div className="user-cabinet__fields user-cabinet__fields--password">
                            {form.password && (
                                <label>
                                    Текущий пароль
                                    <input
                                        type="password"
                                        value={form.current_password}
                                        onChange={handleChange('current_password')}
                                        autoComplete="current-password"
                                    />
                                </label>
                            )}
                            <label>
                                Новый пароль
                                <input
                                    type="password"
                                    value={form.password}
                                    onChange={handleChange('password')}
                                    placeholder="Минимум 8 символов"
                                    autoComplete="new-password"
                                />
                            </label>
                            <label>
                                Подтверждение пароля
                                <input
                                    type="password"
                                    value={form.password_confirm}
                                    onChange={handleChange('password_confirm')}
                                    placeholder="Повторите пароль"
                                    autoComplete="new-password"
                                />
                            </label>
                        </div>

                        <div className="user-cabinet__divider" />

                        <div className="user-cabinet__public user-cabinet__card">
                            <div className="user-cabinet__public-head">
                                <h4>Публичный профиль</h4>
                                <label className="user-cabinet__toggle">
                                    <input
                                        type="checkbox"
                                        checked={form.public_enabled}
                                        onChange={handleChange('public_enabled')}
                                    />
                                    <span>Сделать публичным</span>
                                </label>
                            </div>
                            <div className="muted user-cabinet__hint">
                                Включите публичную страницу и настройте, что показывать.
                            </div>
                            <div className="user-cabinet__inline">
                                <label className="user-cabinet__slug">
                                    Публичная ссылка
                                    <input
                                        type="text"
                                        value={form.public_slug}
                                        onChange={handleChange('public_slug')}
                                        placeholder="user-name"
                                        disabled={user.public_slug_changed}
                                    />
                                </label>
                                <button
                                    type="button"
                                    className="ghost"
                                    disabled={!publicUrl}
                                    onClick={() => onCopy?.(publicUrl, 'Публичная ссылка')}
                                >
                                    Скопировать
                                </button>
                            </div>
                            {user.public_slug_changed && (
                                <div className="muted user-cabinet__hint">
                                    Сменить ссылку можно только один раз.
                                </div>
                            )}
                        </div>

                        <div className="user-cabinet__divider" />

                        <div className="user-cabinet__public-info user-cabinet__card">
                            <div className="user-cabinet__section-title">Публичные сведения</div>
                            <div className="muted user-cabinet__hint">
                                Выбранные пункты будут видны на публичной странице.
                            </div>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_email}
                                    onChange={handleChange('public_show_email')}
                                />
                                <span>Email</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_phone}
                                    onChange={handleChange('public_show_phone')}
                                />
                                <span>Телефон</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_telegram}
                                    onChange={handleChange('public_show_telegram')}
                                />
                                <span>Telegram</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_bio}
                                    onChange={handleChange('public_show_bio')}
                                />
                                <span>О себе</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_agency}
                                    onChange={handleChange('public_show_agency')}
                                />
                                <span>Агентство</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_certificates}
                                    onChange={handleChange('public_show_certificates')}
                                />
                                <span>Сертификаты</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_dashboard_projects}
                                    onChange={handleChange('public_show_dashboard_projects')}
                                />
                                <span>Проекты и кабинеты</span>
                            </label>
                            <label className="user-cabinet__toggle">
                                <input
                                    type="checkbox"
                                    checked={form.public_show_dashboard_cabinets}
                                    onChange={handleChange('public_show_dashboard_cabinets')}
                                />
                                <span>Задачи</span>
                            </label>
                        </div>

                        {notice && (
                            <div className={`user-cabinet__notice user-cabinet__notice--${notice.type}`}>
                                {notice.message}
                            </div>
                        )}

                        <div className="user-cabinet__actions">
                            <button type="submit" disabled={busy || passwordMismatch}>
                                Сохранить
                            </button>
                            <button type="button" className="ghost" onClick={handleReset} disabled={busy}>
                                Сбросить
                            </button>
                        </div>
                    </form>
                </div>

                <aside className="panel user-cabinet__aside">
                    <div className="panel__head">
                        <h4>Сводка</h4>
                    </div>
                    <div className="user-cabinet__summary">
                        <div className="user-cabinet__avatar">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt={displayName} />
                            ) : (
                                initials
                            )}
                        </div>
                        <div>
                            <div className="user-cabinet__name">{displayName}</div>
                            <div className="muted">{user.email}</div>
                            <label className="ghost user-cabinet__avatar-btn">
                                {avatarBusy ? 'Загрузка...' : 'Загрузить аватар'}
                                <input type="file" accept="image/*" onChange={handleAvatarInput} disabled={avatarBusy} />
                            </label>
                        </div>
                    </div>
                    <div className="user-cabinet__badges">
                        <span className={`badge ${user.verified_identity ? 'badge--green' : ''}`}>
                            Авторизованный пользователь
                        </span>
                        <span className={`badge ${user.anylex_recommended ? 'badge--red' : ''}`}>
                            Энилекс рекомендует
                        </span>
                    </div>
                    <div className="user-cabinet__meta">
                        <div>
                            <span className="muted">ID пользователя</span>
                            <strong>{user.id || '-'}</strong>
                        </div>
                        <div>
                            <span className="muted">Компания</span>
                            <strong>{user.company_id || '-'}</strong>
                        </div>
                        <div>
                            <span className="muted">Агентство</span>
                            <strong>{user.agency_name || '-'}</strong>
                        </div>
                        <div>
                            <span className="muted">Статус</span>
                            <strong>{user.is_active ? 'Активен' : 'Неактивен'}</strong>
                        </div>
                    </div>
                    <div className="user-cabinet__lists">
                        <div>
                            <div className="user-cabinet__section-title">Проекты</div>
                            {summaryBusy && <div className="muted">Загрузка...</div>}
                            {!summaryBusy && (!summary?.projects || summary.projects.length === 0) && (
                                <div className="muted">Нет проектов.</div>
                            )}
                            {!summaryBusy && summary?.projects?.map((item) => (
                                <div key={item.id} className="user-cabinet__list-item">
                                    {item.name}
                                    {item.role ? ` | ${formatRoleLabel(item.role)}` : ''}
                                </div>
                            ))}
                        </div>
                        <div>
                            <div className="user-cabinet__section-title">Кабинеты</div>
                            {summaryBusy && <div className="muted">Загрузка...</div>}
                            {!summaryBusy && (!summary?.cabinets || summary.cabinets.length === 0) && (
                                <div className="muted">Нет кабинетов.</div>
                            )}
                            {!summaryBusy && summary?.cabinets?.map((item) => (
                                <div key={item.id} className="user-cabinet__list-item">
                                    {item.name}
                                    {item.marketplace_type ? ` — ${formatMarketplaceType(item.marketplace_type)}` : ''}
                                    {item.role ? ` | ${formatRoleLabel(item.role)}` : ''}
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>

            <div className="user-cabinet__dashboards-grid">
                <div className="panel">
                    <div className="panel__head">
                        <h4>Дашборд проектов и кабинетов</h4>
                    </div>
                    <div className="user-cabinet__metrics">
                        <div>
                            <span className="muted">Проекты</span>
                            <strong>{stats?.projects_count ?? 0}</strong>
                        </div>
                        <div>
                            <span className="muted">Кабинеты</span>
                            <strong>{stats?.cabinets_count ?? 0}</strong>
                        </div>
                    </div>
                </div>
                <div className="panel">
                    <div className="panel__head">
                        <h4>Дашборд задач</h4>
                    </div>
                    <div className="user-cabinet__metrics">
                        <div>
                            <span className="muted">Всего задач</span>
                            <strong>{stats?.tasks_total_count ?? 0}</strong>
                        </div>
                        <div>
                            <span className="muted">В срок</span>
                            <strong>{stats?.tasks_on_time_count ?? 0}</strong>
                        </div>
                        <div>
                            <span className="muted">Процент</span>
                            <strong>{stats?.tasks_on_time_percent ?? 0}%</strong>
                        </div>
                    </div>
                    <div className="metric-bar" aria-hidden="true">
                        <span style={{ width: `${onTimePercent}%` }} />
                    </div>
                </div>
            </div>

            <CaseStudiesPanel onStatusChange={showStatus} />
        </div>
    );
});

export default UserCabinet;
